/**
 * PostesManagerUnifie — LA modale « Postes » / « Gérer », unique et partagée par
 * toutes les briques. Base = PostesDialogGrc (la version complète : affecter un
 * poste + créer/supprimer les postes-modèles).
 *
 * Refonte planning 21/07 : avant, on avait des modales postes divergentes — GRC
 * complète, petite enfance « faiblarde » (juste créer un modèle), urba/compta
 * (PostesSimpleDialog). On unifie tout ici. « Nouveau poste » et « Gérer »
 * ouvrent CETTE modale partout.
 *
 * Elle fait deux choses :
 *   1. AFFECTER un poste (poste + personne + jour + heure) → crée un créneau.
 *   2. GÉRER les postes-modèles glissables (créer / supprimer).
 *
 * Découplée des briques via l'interface `PostesPlanningApi` : chaque brique
 * fournit ses fonctions listPostes / createPoste / deletePoste / createCreneau.
 */
import { useEffect, useState } from 'react';
import { Field, Input, Button, Alert } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import type { PlanningAccent } from '@/components/planning/PlanningPresence';

export type PosteModele = { id: string; titre: string; dureeMin: number; couleur: string | null };

/** Contrat minimal qu'une brique doit fournir pour piloter cette modale. */
export interface PostesPlanningApi {
  listPostes: () => Promise<PosteModele[]>;
  createPoste: (body: { titre: string; dureeMin: number }) => Promise<PosteModele>;
  deletePoste: (id: string) => Promise<void>;
  /** Crée un créneau (= affecter un poste sur le planning). */
  createCreneau: (body: {
    agentUserId: string;
    dateDebut: string;
    dateFin: string;
    titre: string;
    type: string;
    couleurMarker?: string | null;
  }) => Promise<unknown>;
}

const ACCENT_BTN: Record<PlanningAccent, 'tech' | 'grc' | 'ecole' | 'petite_enfance' | 'urba' | 'compta' | 'rh' | 'asso'> = {
  tech: 'tech', grc: 'grc', scolaire: 'ecole', petite_enfance: 'petite_enfance',
  urbanisme: 'urba', comptable: 'compta', rh: 'rh', asso: 'asso',
};

export function PostesManagerUnifie({
  open,
  onClose,
  agents,
  planningApi,
  accent = 'tech',
  typeCreneau = 'presence',
  titre = 'Postes',
  onChanged,
}: {
  open: boolean;
  onClose: () => void;
  agents: Array<{ id: string; fullName: string; role?: string }>;
  planningApi: PostesPlanningApi;
  accent?: PlanningAccent;
  /** Type de créneau créé lors d'une affectation (propre à la brique). */
  typeCreneau?: string;
  titre?: string;
  onChanged: () => void;
}) {
  const [postes, setPostes] = useState<PosteModele[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  // création de poste
  const [nouveauTitre, setNouveauTitre] = useState('');
  const [nouvelleDuree, setNouvelleDuree] = useState(120);
  // affectation
  const [posteId, setPosteId] = useState('');
  const [agentId, setAgentId] = useState('');
  const [jour, setJour] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [heure, setHeure] = useState('09:00');

  const btn = ACCENT_BTN[accent] ?? 'tech';

  useEffect(() => {
    if (!open) return;
    setLoading(true); setError(null); setMsg(null);
    planningApi.listPostes()
      .then((p) => setPostes(p))
      .catch((e) => setError(e instanceof Error ? e.message : 'Erreur'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function creerPoste() {
    if (nouveauTitre.trim().length < 2) { setError('Titre trop court'); return; }
    setError(null); setMsg(null);
    try {
      const p = await planningApi.createPoste({ titre: nouveauTitre.trim(), dureeMin: nouvelleDuree });
      setPostes((prev) => [...prev, p].sort((a, b) => a.titre.localeCompare(b.titre)));
      setNouveauTitre(''); setMsg('Poste créé.');
      onChanged();
    } catch (e) { setError(e instanceof Error ? e.message : 'Erreur'); }
  }

  async function supprimerPoste(id: string) {
    setError(null);
    try {
      await planningApi.deletePoste(id);
      setPostes((prev) => prev.filter((p) => p.id !== id));
      onChanged();
    } catch (e) { setError(e instanceof Error ? e.message : 'Erreur'); }
  }

  async function affecter() {
    const poste = postes.find((p) => p.id === posteId);
    if (!poste) { setError('Choisir un poste'); return; }
    if (!agentId) { setError('Choisir une personne'); return; }
    setError(null); setMsg(null);
    const [h, m] = heure.split(':').map(Number);
    const debut = new Date(`${jour}T${String(h ?? 9).padStart(2, '0')}:${String(m ?? 0).padStart(2, '0')}:00`);
    const fin = new Date(debut.getTime() + poste.dureeMin * 60000);
    try {
      await planningApi.createCreneau({
        agentUserId: agentId,
        dateDebut: debut.toISOString(),
        dateFin: fin.toISOString(),
        titre: poste.titre,
        type: typeCreneau,
        couleurMarker: poste.couleur ?? undefined,
      });
      setMsg('Poste affecté au planning.');
      onChanged();
    } catch (e) { setError(e instanceof Error ? e.message : 'Erreur'); }
  }

  if (!open) return null;
  return (
    <Dialog open={open} onClose={onClose} title={titre} size="lg">
      <div className="flex flex-col gap-5">
        {error && <Alert variant="error">{error}</Alert>}
        {msg && <Alert variant="success">{msg}</Alert>}

        {/* Affecter un poste */}
        <div className="rounded-r-lg border border-border bg-surface/40 p-4">
          <h3 className="mb-3 text-sm font-semibold text-ink">Affecter un poste</h3>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div>
              <label className="mb-1 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Poste</label>
              <select value={posteId} onChange={(e) => setPosteId(e.target.value)} className="h-10 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink">
                <option value="">—</option>
                {postes.map((p) => <option key={p.id} value={p.id}>{p.titre}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Personne</label>
              <select value={agentId} onChange={(e) => setAgentId(e.target.value)} className="h-10 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink">
                <option value="">—</option>
                {agents.map((a) => <option key={a.id} value={a.id}>{a.fullName}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Jour</label>
              <input type="date" value={jour} onChange={(e) => setJour(e.target.value)} className="h-10 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink" />
            </div>
            <div>
              <label className="mb-1 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Heure</label>
              <input type="time" value={heure} onChange={(e) => setHeure(e.target.value)} className="h-10 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink" />
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <Button variant={btn} size="sm" onClick={() => void affecter()}>Affecter au planning</Button>
          </div>
        </div>

        {/* Gérer les postes */}
        <div>
          <h3 className="mb-2 text-sm font-semibold text-ink">Postes disponibles</h3>
          {loading ? <p className="text-sm text-ink-muted">Chargement…</p> : (
            <ul className="mb-3 space-y-1.5">
              {postes.length === 0 && <li className="text-sm text-ink-subtle">Aucun poste. Créez-en un ci-dessous.</li>}
              {postes.map((p) => (
                <li key={p.id} className="flex items-center justify-between rounded-r-md border border-border bg-surface/40 px-3 py-2">
                  <span className="text-sm text-ink">{p.titre} <span className="text-xs text-ink-subtle">· {Math.round(p.dureeMin / 60 * 10) / 10} h</span></span>
                  <button type="button" onClick={() => void supprimerPoste(p.id)} className="text-xs text-danger hover:underline">Supprimer</button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <label className="mb-1 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Nouveau poste</label>
              <input value={nouveauTitre} onChange={(e) => setNouveauTitre(e.target.value)} placeholder="Ex. Accueil du public, Marchés…" className="h-10 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink" />
            </div>
            <div className="w-28">
              <label className="mb-1 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Durée (min)</label>
              <input type="number" min={15} step={15} value={nouvelleDuree} onChange={(e) => setNouvelleDuree(Number(e.target.value))} className="h-10 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink" />
            </div>
            <Button variant="secondary" size="sm" onClick={() => void creerPoste()}>Créer</Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
